import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'

import { mettreAJourProfil } from './api'
import { listerVilles, villeProche, type CatalogueVilles, type Ville } from './apiRecherche'
import { lireCriteres } from './criteres'
import { useLangue } from './LangueContexte'
import type { Langue } from './i18n'

/**
 * La ville courante — L13b, D44 et D45.
 *
 * **Même patron que `LangueContexte`** : la ville est une propriété du document, et c'est au
 * document qu'on la demande. Un écran neuf l'obtient en une ligne, et l'oubli est plus coûteux
 * que le geste correct — un écran qui ne lirait pas la ville chercherait dans la ville par
 * défaut, et personne ne s'en apercevrait à Salé Al Jadida.
 *
 * **La chaîne de résolution, du plus sûr au plus flou** (D45, conception L13 §2.7). Le premier
 * maillon qui répond gagne, et chaque réponse est une proposition : la puce de ville reste
 * visible, un geste la change, le choix est mémorisé.
 *
 * | # | Source | Précision |
 * |---|---|---|
 * | 1 | l'URL (`?city=`) | exacte — une recherche est une URL (L3) |
 * | 2 | le choix mémorisé (`localStorage`), puis la ville du compte connecté | exacte |
 * | 3 | la position de l'appareil — **en silence seulement si la permission est déjà accordée** | ville |
 * | 4 | l'adresse IP (`/cities/nearby`) — souvent muette, livrée désactivée | pays, parfois ville |
 * | 5 | la ville par défaut du référentiel | — |
 *
 * **La position ne quitte jamais l'appareil** (§2.8). Le référentiel arrive entier, avec un
 * centre et un rayon par ville ; la plus proche se calcule ici, par distance orthodromique. Ce
 * que le serveur reçoit du choix final est un code — la même chose qu'il reçoit d'un clic dans
 * la liste. Il n'existe aucune route pour recevoir une coordonnée, et ce n'est pas un oubli.
 *
 * **La permission n'est jamais demandée au chargement.** Chrome et Safari dégradent un prompt
 * sans geste, et un visiteur qui n'a pas encore vu une annonce ferme l'onglet. `detecter()` est
 * appelée par un bouton — « Autour de moi » — et par lui seul. Aux visites suivantes,
 * `permissions.query` permet de résoudre sans rien demander quand elle a déjà été accordée.
 */

/** D'où vient la ville courante. Sert à l'interface (« proposée ») et, un jour, à la mesure. */
export type SourceVille = 'url' | 'stored' | 'account' | 'device' | 'ip' | 'default'

/**
 * Où en est la position de l'appareil.
 *
 * `refusee` et `indisponible` ne sont pas des erreurs : la chaîne continue, et l'écran ne redemande
 * pas. « Autour de moi » reste là pour qui change d'avis. `hors-zone` : une position obtenue,
 * mais aucune ville du référentiel ne contient le point.
 */
export type EtatPosition = 'inconnue' | 'en-cours' | 'ok' | 'refusee' | 'indisponible' | 'hors-zone'

/** La clé de `localStorage` — le choix de ville est la deuxième donnée qu'on y garde, avec la langue. */
const CLE_VILLE = 'coloc:ville'

interface ContexteVille {
  /** Le référentiel entier, villes actives — ouvertes et en amorçage. Vide tant qu'il n'est pas chargé. */
  villes: Ville[]
  /** La ville par défaut du serveur — celle qu'il applique sans paramètre `city`. */
  defaut: string | null
  /** Vrai quand la chaîne a abouti : c'est le signal qu'attend l'écran de recherche avant d'interroger. */
  prete: boolean
  /** La ville courante. `null` tant que `prete` est faux, ou si le référentiel n'a pas pu être lu. */
  courante: Ville | null
  source: SourceVille | null
  etatPosition: EtatPosition
  /** Adopte une ville. `source` dit si c'est un choix (mémorisé) ou une lecture (pas mémorisée). */
  choisir: (code: string, source?: SourceVille) => void
  /** Demande la position — sur un GESTE, jamais au chargement — et adopte la ville trouvée. */
  detecter: () => Promise<Ville | null>
  /** La ville active la plus proche dont le rayon contient le point. `null` hors de tout rayon. */
  plusProche: (latitude: number, longitude: number) => Ville | null
  /** La ville OUVERTE la plus proche d'une autre — ce que l'écran « pas encore ouverte » propose. */
  plusProcheOuverte: (ville: Ville) => { ville: Ville; km: number } | null
  trouver: (code: string | null | undefined) => Ville | undefined
  /** Le libellé dans la langue courante. Repli sur le code quand la ville est inconnue — jamais une chaîne vide. */
  libelle: (code: string | null | undefined) => string
}

const VilleContexte = createContext<ContexteVille | null>(null)

/**
 * Le libellé d'une ville dans une langue. Le référentiel porte les trois : jamais de
 * translittération côté client, et le repli est le français, pas le code.
 */
export function libelleVille(ville: Ville, langue: Langue): string {
  if (langue === 'ar' && ville.labelAr !== '') {
    return ville.labelAr
  }

  if (langue === 'en' && ville.labelEn !== '') {
    return ville.labelEn
  }

  return ville.labelFr
}

/**
 * Distance orthodromique, en mètres. Formule de haversine, rayon terrestre moyen.
 *
 * La même règle que `City.Covers` côté serveur, et c'est le point (§2.8) : écrite en PostGIS
 * là-bas et ici en TypeScript, elle ne doit pas diverger, sans quoi la ville proposée par le
 * téléphone cesserait d'être celle que le serveur aurait dite.
 */
export function distanceMetres(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const rad = Math.PI / 180
  const dLat = (lat2 - lat1) * rad
  const dLng = (lng2 - lng1) * rad
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2

  return 2 * 6_371_000 * Math.asin(Math.sqrt(a))
}

/** Parmi les villes dont le rayon contient le point, la plus proche du centre. */
export function villeContenant(villes: Ville[], latitude: number, longitude: number): Ville | null {
  let meilleure: Ville | null = null
  let distanceMin = Number.POSITIVE_INFINITY

  for (const ville of villes) {
    const distance = distanceMetres(latitude, longitude, ville.latitude, ville.longitude)

    if (distance <= ville.radiusMetres && distance < distanceMin) {
      meilleure = ville
      distanceMin = distance
    }
  }

  return meilleure
}

function lirePosition(): Promise<GeolocationPosition> {
  return new Promise((resoudre, rejeter) =>
    navigator.geolocation.getCurrentPosition(resoudre, rejeter, {
      // On cherche une ville, pas une rue : la précision basse suffit, et elle ne réveille pas le
      // GPS. Une position d'il y a dix minutes désigne la même ville.
      enableHighAccuracy: false,
      timeout: 8_000,
      maximumAge: 10 * 60 * 1_000,
    }),
  )
}

/**
 * La position, SANS rien demander : seulement si la permission est déjà accordée.
 *
 * `permissions.query` n'existe pas partout (Safari l'a depuis 16) : sans elle, on ne tente rien —
 * tenter, c'est demander, et demander au chargement est ce que D45 interdit.
 */
async function positionSiDejaPermise(): Promise<GeolocationPosition | null> {
  if (!('geolocation' in navigator) || navigator.permissions?.query === undefined) {
    return null
  }

  try {
    const statut = await navigator.permissions.query({ name: 'geolocation' })

    if (statut.state !== 'granted') {
      return null
    }

    return await lirePosition()
  } catch {
    return null
  }
}

function lireMemoire(): string | null {
  try {
    return globalThis.localStorage.getItem(CLE_VILLE)
  } catch {
    return null
  }
}

function ecrireMemoire(code: string): void {
  try {
    globalThis.localStorage.setItem(CLE_VILLE, code)
  } catch {
    /* stockage refusé : la ville suivra l'URL, sans mémoire au-delà de la session */
  }
}

/**
 * Le fournisseur.
 *
 * `pret` et `preferenceCompte` viennent de la coquille : la chaîne ne démarre qu'une fois la
 * session reconstituée, parce que la ville du compte est un maillon et qu'elle n'est connue
 * qu'après. Démarrer avant, c'est interroger l'IP pour quelqu'un dont le compte savait déjà.
 */
export function FournisseurDeVille({
  pret,
  connecte,
  preferenceCompte,
  children,
}: {
  pret: boolean
  connecte: boolean
  preferenceCompte: string | null | undefined
  children: ReactNode
}) {
  const langue = useLangue()
  const { pathname, search } = useLocation()

  // Un seul état pour « lu » et « ce qui a été lu » : deux états séparés laisseraient un rendu
  // où le référentiel est déclaré lu sans l'être encore, et la chaîne partirait sans villes.
  const [lecture, setLecture] = useState<{ lu: boolean; catalogue: CatalogueVilles | null }>({
    lu: false,
    catalogue: null,
  })
  const { lu: catalogueLu, catalogue } = lecture
  const [code, setCode] = useState<string | null>(null)
  const [source, setSource] = useState<SourceVille | null>(null)
  const [resolue, setResolue] = useState(false)
  const [etatPosition, setEtatPosition] = useState<EtatPosition>('inconnue')

  // La chaîne tourne UNE fois. Un `useEffect` qui dépendrait de l'URL la rejouerait à chaque
  // navigation, et une ville détectée serait remplacée par la ville par défaut au premier clic.
  const chaineLancee = useRef(false)
  const connecteRef = useRef(connecte)
  connecteRef.current = connecte

  useEffect(() => {
    let annule = false

    void listerVilles()
      .then((lu) => {
        if (!annule) {
          setLecture({ lu: true, catalogue: lu })
        }
      })
      .catch(() => {
        if (!annule) {
          setLecture({ lu: true, catalogue: null })
        }
      })

    return () => {
      annule = true
    }
  }, [])

  const villes = useMemo(() => catalogue?.items ?? [], [catalogue])

  const trouver = useCallback(
    (candidat: string | null | undefined) =>
      candidat === null || candidat === undefined
        ? undefined
        : villes.find((ville) => ville.code === candidat.toUpperCase()),
    [villes],
  )

  const plusProche = useCallback(
    (latitude: number, longitude: number) => villeContenant(villes, latitude, longitude),
    [villes],
  )

  const choisir = useCallback(
    (nouveau: string, provenance: SourceVille = 'stored') => {
      const ville = trouver(nouveau)

      if (ville === undefined) {
        return
      }

      setCode(ville.code)
      setSource(provenance)

      // Seuls un CHOIX et une DÉTECTION se mémorisent. Une URL reçue n'est pas un choix — le lien
      // d'un ami vers Casablanca ne doit pas déplacer ma ville — et une proposition de l'IP ou du
      // défaut, mémorisée, ne serait plus jamais remise en question.
      if (provenance === 'stored' || provenance === 'device') {
        ecrireMemoire(ville.code)

        // Sur le compte aussi, pour que l'application mobile la retrouve (D45). Sans attendre :
        // la ville est déjà à l'écran, et un échec ici n'a rien à dire à l'utilisateur.
        if (connecteRef.current) {
          void mettreAJourProfil({ preferredCityCode: ville.code }).catch(() => undefined)
        }
      }
    },
    [trouver],
  )

  const detecter = useCallback(async (): Promise<Ville | null> => {
    if (!('geolocation' in navigator)) {
      setEtatPosition('indisponible')
      return null
    }

    setEtatPosition('en-cours')

    try {
      const position = await lirePosition()
      const ville = villeContenant(villes, position.coords.latitude, position.coords.longitude)

      if (ville === null) {
        setEtatPosition('hors-zone')
        return null
      }

      setEtatPosition('ok')
      choisir(ville.code, 'device')
      return ville
    } catch (cause) {
      const refus =
        typeof cause === 'object' &&
        cause !== null &&
        'code' in cause &&
        (cause as GeolocationPositionError).code === 1

      setEtatPosition(refus ? 'refusee' : 'indisponible')
      return null
    }
  }, [villes, choisir])

  // La chaîne. Elle attend le référentiel ET la session, puis tourne une seule fois.
  useEffect(() => {
    if (!catalogueLu || !pret || chaineLancee.current) {
      return
    }

    chaineLancee.current = true

    const fixer = (candidat: string | null | undefined, provenance: SourceVille): boolean => {
      const ville = trouver(candidat)

      if (ville === undefined) {
        return false
      }

      setCode(ville.code)
      setSource(provenance)
      setResolue(true)
      return true
    }

    // Référentiel illisible : on ne peut rien résoudre. `prete` passe quand même à vrai, pour que
    // la recherche parte sans ville — le serveur appliquera la sienne et le dira.
    if (catalogue === null) {
      setResolue(true)
      return
    }

    // 1. L'URL, sur l'écran de recherche seulement : ailleurs, `?city=` ne veut rien dire.
    if (pathname.startsWith('/recherche') && fixer(lireCriteres(search).cityCode, 'url')) {
      return
    }

    // 2. Le choix mémorisé sur cet appareil, puis la ville du compte.
    if (fixer(lireMemoire(), 'stored') || fixer(preferenceCompte, 'account')) {
      return
    }

    let annule = false

    void (async () => {
      // 3. La position — en silence, et seulement si la permission est déjà accordée. Ce maillon
      //    passe par `fixer` et non par `choisir` : une lecture silencieuse n'est pas un choix, elle
      //    ne se mémorise pas — et elle se refait à chaque visite, ce qui suit qui déménage.
      const position = await positionSiDejaPermise()

      if (annule) {
        return
      }

      if (position !== null) {
        const ville = villeContenant(catalogue.items, position.coords.latitude, position.coords.longitude)

        if (ville !== null) {
          setEtatPosition('ok')
          fixer(ville.code, 'device')
          return
        }

        setEtatPosition('hors-zone')
      }

      // 4. L'IP — un indice, souvent muet.
      const indice = await villeProche()

      if (annule) {
        return
      }

      if (indice.cityCode !== null && fixer(indice.cityCode, 'ip')) {
        return
      }

      // 5. La ville par défaut.
      if (!fixer(catalogue.defaultCityCode, 'default')) {
        setResolue(true)
      }
    })()

    return () => {
      annule = true
    }
  }, [catalogueLu, catalogue, pret, pathname, search, preferenceCompte, trouver])

  const courante = useMemo(() => trouver(code) ?? null, [trouver, code])

  const plusProcheOuverte = useCallback(
    (ville: Ville) => {
      let meilleure: { ville: Ville; km: number } | null = null

      for (const candidate of villes) {
        if (candidate.status !== 'Open' || candidate.code === ville.code) {
          continue
        }

        const km =
          distanceMetres(ville.latitude, ville.longitude, candidate.latitude, candidate.longitude) /
          1_000

        if (meilleure === null || km < meilleure.km) {
          meilleure = { ville: candidate, km }
        }
      }

      return meilleure
    },
    [villes],
  )

  const libelle = useCallback(
    (candidat: string | null | undefined) => {
      const ville = trouver(candidat)

      return ville === undefined ? (candidat ?? '') : libelleVille(ville, langue)
    },
    [trouver, langue],
  )

  const valeur = useMemo<ContexteVille>(
    () => ({
      villes,
      defaut: catalogue?.defaultCityCode ?? null,
      prete: resolue,
      courante,
      source,
      etatPosition,
      choisir,
      detecter,
      plusProche,
      plusProcheOuverte,
      trouver,
      libelle,
    }),
    [
      villes,
      catalogue,
      resolue,
      courante,
      source,
      etatPosition,
      choisir,
      detecter,
      plusProche,
      plusProcheOuverte,
      trouver,
      libelle,
    ],
  )

  return <VilleContexte.Provider value={valeur}>{children}</VilleContexte.Provider>
}

/** La ville courante et le référentiel. Hors du fournisseur, c'est une erreur de câblage, pas un cas. */
export function useVille(): ContexteVille {
  const contexte = useContext(VilleContexte)

  if (contexte === null) {
    throw new Error('useVille : aucun FournisseurDeVille au-dessus de ce composant.')
  }

  return contexte
}
